// widgets/asset-details/SubmitMileageAccordionTable.tsx
import {
  ClockCounterClockwise,
  CaretDown,
  Speedometer,
  CaretUp,
  PencilSimple,
  X,
  CheckCircle,
  Clock,
} from '@phosphor-icons/react';
import React, { useCallback, useState } from 'react';
import { FieldErrors, UseFormRegister } from 'react-hook-form';
import {
  MileageReportRequest,
  MileageReportRequestUpdate,
  RecentMileageSnapshots,
} from '../../SubmitMileage';
import { formatNumberWithCommas } from '../../../../../../../utils/CurrencyUtils';
import { Pagination, Select, Tooltip } from 'antd';
import { ClipLoader } from 'react-spinners';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../../../../../../utils/DateUtils';
import MileageInput from './MileageInput';

interface Props {
  mileageReportRequests: MileageReportRequest[];
  control: any;
  register?: UseFormRegister<{ assets: MileageReportRequestUpdate[] }>;
  errors?: FieldErrors<{ assets: MileageReportRequestUpdate[] }>;
  mileageReportRequestsCount: number;
  isLoadingCount: boolean;
  offset: number;
  limit: number;
  perPage: number;
  handlePerPageChange: (value: number) => void;
  handlePageChange: (page: number, pageSize: number) => void;
  editingRows: Set<string>;
  onToggleEditing: (truckId: string) => void;
  /** THT-678: Catena-connected customers can't submit mileage manually */
  catenaConnected?: boolean;
}

const HEADERS = [
  'Type',
  'Unit Number',
  'Submitted By',
  'Driver Requested',
  'Previous Odometer',
  'Current Odometer',
  'Status',
  'History',
];

const HISTORY_HEADERS = [
  'Requested On',
  'Submitted on',
  'Mileage',
  'Submitted By',
];

export const SubmitMileageAccordionTable: React.FC<Props> = ({
  mileageReportRequests,
  control,
  register,
  errors,
  offset,
  limit,
  mileageReportRequestsCount,
  isLoadingCount,
  perPage,
  handlePerPageChange,
  handlePageChange,
  editingRows,
  onToggleEditing,
  catenaConnected = false,
}) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const toggle = (truckId: string) =>
    setExpanded((s) => ({ ...s, [truckId]: !s[truckId] }));

  const isSubmitted = (request: RecentMileageSnapshots | null): boolean => {
    if (!request) return false;
    return (
      request.status === 'submitted' ||
      (request.cumulative_odometer != null && request.miles != null)
    );
  };

  const renderPagination = () => (
    <div className="flex w-full items-center justify-between gap-4 rounded-b-[8px] border-[1px] border-bordergray bg-white px-4 py-3 sm:flex-col md:flex-row">
      {!isLoadingCount ? (
        <div className="flex flex-row items-center space-x-2 sm:min-h-[48px] sm:w-full sm:justify-between md:w-[50%]">
          {mileageReportRequestsCount > 0 && (
            <p>
              <span className="text-graytertiary">Showing</span> {offset + 1} -{' '}
              {Math.min(offset + limit, mileageReportRequestsCount)}{' '}
              <span className="text-graytertiary">of</span>{' '}
              {mileageReportRequestsCount}
            </p>
          )}
          <Select
            value={perPage}
            onChange={handlePerPageChange}
            placeholder="Rows per page"
            className="w-[40%]"
          >
            <Select.Option value={20}>20 per page</Select.Option>
            <Select.Option value={50}>50 per page</Select.Option>
            <Select.Option value={100}>100 per page</Select.Option>
          </Select>
        </div>
      ) : (
        <ClipLoader
          color="#1D213B"
          loading={isLoadingCount}
          size={20}
          aria-label="Loading Spinner"
          data-testid="loader"
          className="flex h-full w-full items-center justify-center"
        />
      )}
      <Pagination
        current={Math.floor(offset / limit) + 1}
        pageSize={perPage}
        total={mileageReportRequestsCount}
        onChange={handlePageChange}
        showSizeChanger={false}
      />
    </div>
  );

  const sortSnapsDesc = (
    snaps: MileageReportRequest['recentMileageSnapshots']
  ) =>
    (snaps || [])
      .slice()
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

  const driverName = useCallback((mileageRequest: RecentMileageSnapshots) => {
    // Check if driver info is available (including primary owner's driver info from backend)
    if (
      mileageRequest?.driver?.first_name &&
      mileageRequest?.driver?.last_name &&
      mileageRequest?.manually_completed_at?.length
    ) {
      return `${mileageRequest.driver.first_name} ${mileageRequest.driver.last_name}`;
    }
    return '--';
  }, []);

  const renderRequestedDriverName = useCallback(
    (mileageRequest: RecentMileageSnapshots) => {
      if (mileageRequest?.driver == null) {
        return '--';
      }
      return `${mileageRequest?.driver?.first_name} ${mileageRequest?.driver?.last_name}`;
    },
    []
  );

  return (
    <div className="flex h-full w-full flex-col justify-between overflow-hidden rounded-[8px] border-l-[1px] border-r-[1px] border-t-[1px] border-bordergray bg-white">
      {mileageReportRequests && mileageReportRequests.length > 0 ? (
        <>
          <div className="w-full overflow-x-auto">
            <table className="w-[800px] min-w-full table-auto rounded-lg bg-white shadow-md">
              <thead className="bg-gray-100">
                <tr>
                  <th className="w-[36px]" />
                  {HEADERS.map((header) => (
                    <th
                      key={header}
                      className="px-4 py-2 text-left text-[14px] font-[600] text-primary last:text-right"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="overflow-y-auto">
                {mileageReportRequests.map((asset, index) => {
                  const snaps = sortSnapsDesc(asset.recentMileageSnapshots);
                  const mostRecentRequest = asset?.mostRecentRequest;

                  const truckId = asset.truck_id || String(index);
                  const isOpen = !!expanded[truckId];

                  return (
                    <React.Fragment key={truckId}>
                      {/* Main row */}
                      <tr className="border-b border-gray-200 hover:bg-ctaactivegreen">
                        <td
                          className="cursor-pointer px-2 py-2 align-middle"
                          onClick={() => toggle(truckId)}
                          aria-label={isOpen ? 'Collapse' : 'Expand'}
                        >
                          {isOpen ? (
                            <CaretUp size={18} className="text-primary" />
                          ) : (
                            <CaretDown size={18} className="text-primary" />
                          )}
                        </td>

                        <td className="px-4 py-2 text-[14px] text-primary">
                          <img
                            alt="truck-svg"
                            src="/truck.svg"
                            className="h-[24px] w-[24px]"
                          />
                        </td>
                        <td className="px-4 py-2 text-[14px] text-primary">
                          {asset.truck_display_name}
                        </td>
                        <td className="px-4 py-2 text-[14px] text-primary">
                          {driverName(
                            mostRecentRequest ?? ({} as RecentMileageSnapshots)
                          )}
                        </td>
                        <td className="px-4 py-2 text-[14px] text-primary">
                          {renderRequestedDriverName(
                            mostRecentRequest ?? ({} as RecentMileageSnapshots)
                          )}
                        </td>

                        <td className="px-4 py-2 text-[14px] text-primary">
                          {(asset.previous_odometer ?? asset.current_mileage) !=
                          null
                            ? formatNumberWithCommas(
                                asset.previous_odometer ??
                                  asset.current_mileage ??
                                  0
                              )
                            : '--'}
                        </td>

                        <td className="w-[200px] px-4 py-2 text-[14px] text-primary">
                          {/* Hidden fields for PATCH payload */}
                          <input
                            type="hidden"
                            value={asset.truck_id ?? ''}
                            {...register?.(`assets.${index}.truck_id`)}
                          />
                          <input
                            type="hidden"
                            value={mostRecentRequest?.snapshot_id ?? ''}
                            {...register?.(`assets.${index}.snapshot_id`)}
                          />
                          {mostRecentRequest?.created_at?.length && (
                            <MileageInput
                              name={`assets.${index}.cumulative_odometer`}
                              control={control}
                              index={index}
                              errors={errors}
                              defaultValue={
                                mostRecentRequest?.cumulative_odometer ?? ''
                              }
                              previousOdometer={
                                asset.previous_odometer ?? asset.current_mileage
                              }
                              disabled={
                                catenaConnected ||
                                (isSubmitted(mostRecentRequest) &&
                                  !editingRows.has(truckId))
                              }
                            />
                          )}
                        </td>

                        {/* Status column */}
                        <td className="px-4 py-2 text-[14px]">
                          <div className="flex items-center gap-2">
                            {isSubmitted(mostRecentRequest) ? (
                              <>
                                {editingRows.has(truckId) ? (
                                  <>
                                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-700">
                                      <PencilSimple size={12} weight="bold" />
                                      Editing
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => onToggleEditing(truckId)}
                                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                                      title="Cancel editing"
                                    >
                                      <X size={14} />
                                      Cancel
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-700">
                                      <CheckCircle size={12} weight="fill" />
                                      Submitted
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => onToggleEditing(truckId)}
                                      disabled={catenaConnected}
                                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
                                      title="Edit mileage"
                                    >
                                      <PencilSimple size={14} />
                                      Edit
                                    </button>
                                  </>
                                )}
                              </>
                            ) : mostRecentRequest?.created_at ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-700">
                                <Clock size={12} weight="fill" />
                                Pending
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">--</span>
                            )}
                          </div>
                        </td>

                        <td className="cursor-pointer px-4 py-2 text-right text-[14px] text-primary">
                          <Tooltip title="See Odometer History">
                            <button
                              type="button"
                              className="ml-auto rounded-full p-1 hover:bg-gray-100"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate(
                                  `/dashboard/assets/odometer-history/${asset.truck_id}`
                                );
                              }}
                            >
                              <ClockCounterClockwise size={20} />
                            </button>
                          </Tooltip>
                        </td>
                      </tr>

                      {/* Expanded history */}
                      {isOpen && (
                        <tr className="border-b border-gray-200">
                          <td className="bg-secondarybg p-0" />{' '}
                          {/* caret spacer */}
                          <td
                            colSpan={HEADERS.length}
                            className="bg-secondarybg p-0"
                          >
                            <div className="w-full overflow-x-auto">
                              <table className="min-w-full table-auto">
                                <thead>
                                  <tr className="bg-transparent">
                                    {HISTORY_HEADERS.map((h) => (
                                      <th
                                        key={h}
                                        className="px-4 py-2 text-left text-[12px] font-[500] text-graytertiary"
                                      >
                                        {h}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {snaps.map((s, i) => {
                                    const created = s.created_at
                                      ? new Date(s.created_at)
                                      : undefined;
                                    const submitted = s.manually_completed_at
                                      ? new Date(s.manually_completed_at)
                                      : undefined;
                                    return (
                                      <tr key={`${s.snapshot_id ?? i}`}>
                                        <td className="px-4 py-2 text-sm text-primary">
                                          {created
                                            ? formatDate(created, true, true)
                                            : '--'}
                                        </td>
                                        <td
                                          className={`px-4 py-2 text-sm ${s.status === 'pending' ? 'text-orange-500' : 'text-primary'}`}
                                        >
                                          {s.status === 'pending'
                                            ? 'Mileage not submitted'
                                            : submitted
                                              ? formatDate(
                                                  submitted,
                                                  true,
                                                  true
                                                )
                                              : '--'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-primary">
                                          {s.cumulative_odometer != null
                                            ? formatNumberWithCommas(
                                                s.cumulative_odometer
                                              )
                                            : '--'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-primary">
                                          {driverName(s)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                  {snaps.length === 0 && (
                                    <tr>
                                      <td
                                        colSpan={HISTORY_HEADERS.length}
                                        className="px-4 py-3 text-[13px] text-graysecondary"
                                      >
                                        No history for this truck.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {renderPagination()}
        </>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center space-y-2">
          <div className="flex h-[64px] w-[64px] items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
            <Speedometer size={40} className="text-graysecondary" />
          </div>
          <p className="text-[14px] leading-[20px] text-graysecondary">
            No mileage report requests found.
          </p>
        </div>
      )}
    </div>
  );
};

export default SubmitMileageAccordionTable;
